import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { Clock, Check, X } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function localDayKey() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function localNowIso() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

export default function ScheduleLockOverlay({ onUpdate }) {
  const [lock, setLock] = useState({ is_locked: false });
  const [busy, setBusy] = useState(false);
  const pollRef = useRef(null);

  const check = useCallback(async () => {
    try {
      const { data } = await axios.post(`${API}/schedule-template/lock-status`,
        { date: localDayKey(), local_now: localNowIso() },
        { withCredentials: true });
      setLock(data || { is_locked: false });
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    check();
    pollRef.current = setInterval(check, 30000);
    const onVis = () => { if (document.visibilityState === 'visible') check(); };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      clearInterval(pollRef.current);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [check]);

  const markDone = async () => {
    if (!lock.time) return;
    setBusy(true);
    try {
      await axios.post(`${API}/schedule-template/toggle`,
        { time: lock.time, date: localDayKey(), done: true },
        { withCredentials: true });
      toast.success(`✓ ${lock.text}`, { duration: 2000 });
      await check();
      onUpdate?.();
    } catch { toast.error('Kunde inte bocka av'); }
    finally { setBusy(false); }
  };

  const skip = async () => {
    if (!lock.time) return;
    setBusy(true);
    try {
      await axios.post(`${API}/schedule-template/skip`,
        { time: lock.time, date: localDayKey() },
        { withCredentials: true });
      toast('Lugnt. Vi tar nästa 💛', { duration: 2000 });
      await check();
      onUpdate?.();
    } catch { /* ignore */ }
    finally { setBusy(false); }
  };

  if (!lock.is_locked) return null;

  const mins = lock.minutes_overdue || 0;
  const timeAgo = mins < 60 ? `${mins} min sedan` : `${Math.floor(mins / 60)}h ${mins % 60}m sedan`;

  return (
    <div
      className="fixed inset-0 z-[210] bg-gradient-to-br from-[#FFF5E6] via-[#FFE8F5] to-[#F5EEFF] flex items-center justify-center p-6 overflow-auto"
      data-testid="schedule-hard-lock"
    >
      {/* Ambient sparkles */}
      <div className="absolute inset-0 pointer-events-none opacity-40">
        {Array.from({ length: 10 }).map((_, i) => (
          <span
            key={i}
            className="absolute text-2xl"
            style={{
              left: `${(i * 43) % 100}%`,
              top: `${(i * 59) % 100}%`,
              animation: `sch-float ${7 + (i % 4)}s ease-in-out infinite`,
              animationDelay: `${i * 0.3}s`,
            }}
          >
            ⏰
          </span>
        ))}
      </div>

      <div className="relative max-w-md w-full space-y-5">
        <div className="text-center">
          <div className="mx-auto w-24 h-24 rounded-full bg-[#FFC8DD] border-4 border-black flex items-center justify-center text-5xl shadow-[0_6px_0_0_rgba(0,0,0,1)] animate-sch-wobble">
            ⏰
          </div>
          <p className="mt-3 text-xs font-bold uppercase tracking-wider opacity-60">
            Dagens schema · kl {lock.time}
          </p>
          <h2 className="text-2xl font-bold font-['Fredoka'] mt-1">{lock.message}</h2>
          <p className="text-xs text-gray-600 mt-1 flex items-center justify-center gap-1">
            <Clock className="w-3 h-3" /> {timeAgo}
            {lock.total_overdue > 1 && <span className="ml-2 opacity-60">· +{lock.total_overdue - 1} fler att hinna</span>}
          </p>
        </div>

        <div className="neo-card p-4 bg-white text-center">
          <p className="text-[11px] font-bold uppercase opacity-60 mb-1">Nu är det dags för</p>
          <p className="text-lg font-bold font-['Fredoka']">{lock.text}</p>
        </div>

        <button
          onClick={markDone}
          disabled={busy}
          className="w-full neo-btn py-4 bg-[#B9FBC0] text-base font-bold flex items-center justify-center gap-2"
          data-testid="schedule-lock-done"
        >
          <Check className="w-5 h-5" strokeWidth={3} /> Klart! Släpp in mig
        </button>

        <button
          onClick={skip}
          disabled={busy}
          className="w-full neo-btn py-2 bg-white text-sm flex items-center justify-center gap-2"
          data-testid="schedule-lock-skip"
        >
          <X className="w-4 h-4" /> Jag kan inte just nu 💛
        </button>
        <p className="text-[10px] text-center text-gray-500 px-4">
          Schemat finns där imorgon också. Ingen skam 🌸
        </p>
      </div>

      <style>{`
        @keyframes sch-wobble { 0%,100% { transform: rotate(-3deg); } 50% { transform: rotate(3deg) translateY(-3px); } }
        @keyframes sch-float { 0%,100% { transform: translateY(0) rotate(0deg); } 50% { transform: translateY(-15px) rotate(10deg); } }
        .animate-sch-wobble { animation: sch-wobble 2.4s ease-in-out infinite; }
      `}</style>
    </div>
  );
}
